import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSpring, animated } from '@react-spring/web';
import api from '../api/axios';
import { validateImageFile, formatFileSize } from '../utils/fileValidation';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useToastContext } from '../contexts/ToastContext';
import { sanitizeInput } from '../config/security';

// Sanitize input to prevent XSS
const sanitizeFormInput = (input) => {
  if (typeof input !== 'string') return input;
  return input.replace(/<script[^>]*>.*?<\/script>/gi, '')
    .replace(/<[^>]*>/g, '')
    .trim();
};

export default function UserForm({ user, onSave, onCancel }) {
  const { isDark } = useTheme();
  const { t } = useLanguage();
  const toast = useToastContext();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    profile_photo: '',
    status: 'active'
  });

  useEffect(() => {
    if (user) {
      setFormData({
        name: sanitizeFormInput(user.name) || '',
        email: sanitizeFormInput(user.email) || '',
        password: '',
        phone: sanitizeFormInput(user.phone) || '',
        profile_photo: user.profile_photo || '',
        status: user.status || 'active'
      });
      setSelectedFile(null);
      setPreviewUrl(null);
    }
  }, [user]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const progressSpring = useSpring({
    width: loading ? '100%' : '0%',
    config: { tension: 200, friction: 25 }
  });

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    setError('');

    if (!file) {
      setSelectedFile(null);
      setPreviewUrl(null);
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please select a valid image file (JPEG, PNG, GIF, WebP).');
      e.target.value = '';
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setError('Image size must be less than 2MB.');
      e.target.value = '';
      return;
    }

    try {
      setSelectedFile(file);

      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const dataUrl = event.target.result;
          if (dataUrl && typeof dataUrl === 'string' && dataUrl.startsWith('data:image/')) {
            const base64Data = dataUrl.split(',')[1];
            if (base64Data && /^[A-Za-z0-9+/]*={0,2}$/.test(base64Data) && dataUrl.length < 5 * 1024 * 1024) {
              setPreviewUrl(dataUrl);
            } else {
              throw new Error('Invalid image data format or size too large');
            }
          } else {
            throw new Error('Invalid image data');
          }
        } catch (err) {
          setError('Failed to process image file.');
          setSelectedFile(null);
          setPreviewUrl(null);
        }
      };

      reader.onerror = () => {
        setError('Failed to read image file.');
        setSelectedFile(null);
        setPreviewUrl(null);
        e.target.value = '';
      };

      reader.readAsDataURL(file);
    } catch (error) {
      setError('Invalid image file or corrupted file.');
      setSelectedFile(null);
      setPreviewUrl(null);
      e.target.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      let photoUrl = formData.profile_photo;

      if (selectedFile) {
        try {
          photoUrl = await convertFileToBase64(selectedFile);
        } catch (error) {
          setError('Failed to process image file');
          setLoading(false);
          return;
        }
      }

      const submitData = {
        name: sanitizeFormInput(formData.name),
        email: sanitizeFormInput(formData.email),
        phone: formData.phone ? sanitizeFormInput(formData.phone) : null,
        status: formData.status
      };

      if (!submitData.name || !submitData.email) {
        throw new Error('Name and email are required');
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(submitData.email)) {
        throw new Error('Please enter a valid email address');
      }

      if (formData.password || !user) {
        submitData.password = formData.password;
      }

      if (photoUrl) {
        submitData.profile_photo = photoUrl;
      }

      let response;
      if (user) {
        const sanitizedId = encodeURIComponent(String(user.id));
        response = await api.put(`/users/${sanitizedId}`, submitData);
      } else {
        if (!formData.password) {
          throw new Error('Password is required for new users');
        }
        response = await api.post('/users', submitData);
      }

      toast.success('User berhasil disimpan!');
      onSave();
    } catch (error) {
      const errorMsg = error.response?.data?.errors ?
        Object.values(error.response.data.errors).flat().join(', ') :
        error.response?.data?.message || 'Failed to save user';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
        onClick={(e) => e.target === e.currentTarget && onCancel()}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 w-full max-w-2xl max-h-[90vh] overflow-y-auto mt-20"
        >
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="sticky top-0 bg-white/95 backdrop-blur-xl border-b border-gray-200 p-6 rounded-t-2xl"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: "spring", damping: 15, delay: 0.2 }}
                  className={`w-12 h-12 rounded-xl flex items-center justify-center mr-4 shadow-lg ${isDark ? 'bg-gradient-to-br from-gray-600 to-gray-700' : 'bg-gradient-to-br from-blue-500 to-blue-600'}`}
                >
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <h2 className={`text-2xl font-bold ${isDark ? 'text-gray-100' : 'bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent'}`}>{user ? t('edit_user') : t('add_user')}</h2>
                  <p className="text-gray-500 text-sm">{t('manage_user_info')}</p>
                </motion.div>
              </div>
              <motion.button
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4 }}
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
                onClick={onCancel}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </motion.button>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="p-6"
          >
            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.95 }}
                  className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl"
                >
                  <div className="flex items-center">
                    <motion.svg
                      initial={{ rotate: -90 }}
                      animate={{ rotate: 0 }}
                      className="w-5 h-5 mr-2"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </motion.svg>
                    {error}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <motion.form
              onSubmit={handleSubmit}
              className="space-y-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, staggerChildren: 0.1 }}
            >
              {loading && (
                <div className="w-full bg-gray-200 rounded-full h-1 mb-4">
                  <animated.div
                    style={progressSpring}
                    className="bg-gradient-to-r from-blue-500 to-blue-600 h-1 rounded-full"
                  />
                </div>
              )}

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-blue-50 p-6 rounded-xl border border-blue-100"
              >
                <label className="block text-sm font-semibold text-gray-700 mb-3">{t('profile_photo')}</label>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    className="flex-shrink-0"
                  >
                    {previewUrl ? (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="text-center"
                      >
                        <img
                          src={previewUrl}
                          alt="New Preview"
                          className="w-20 h-20 rounded-xl object-cover border-2 border-blue-300 shadow-lg"
                          onError={() => {
                            setError('Failed to display image preview.');
                            setPreviewUrl(null);
                          }}
                        />
                        <p className="text-xs text-blue-600 mt-1 font-medium">{t('new_photo')}</p>
                      </motion.div>
                    ) : formData.profile_photo ? (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="text-center"
                      >
                        <img
                          src={formData.profile_photo}
                          alt="Current"
                          className="w-20 h-20 rounded-xl object-cover border-2 border-gray-300 shadow-lg"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'block';
                          }}
                          referrerPolicy="no-referrer"
                        />
                        <div className="hidden text-xs text-red-500 mt-1">{t('invalid_photo')}</div>
                        <p className="text-xs text-gray-600 mt-1">{t('current_photo')}</p>
                      </motion.div>
                    ) : (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="w-20 h-20 bg-gradient-to-br from-gray-200 to-gray-300 rounded-xl flex items-center justify-center"
                      >
                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </motion.div>
                    )}
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 }}
                    className="flex-1 w-full"
                  >
                    <input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                      onChange={handleFileChange}
                      className={`w-full p-3 border-2 border-dashed rounded-xl transition-colors file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium ${isDark ? 'border-gray-600 bg-gray-700/50 hover:border-gray-500 file:bg-gray-600 file:text-gray-200 hover:file:bg-gray-500 text-gray-300' : 'border-blue-300 bg-white/50 hover:border-blue-400 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100'}`}
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      {t('choose_image_file_user')}
                      {selectedFile && (
                        <span className="block text-blue-600 font-medium mt-1">
                          {t('selected_file')}: {selectedFile.name} ({formatFileSize(selectedFile.size)})
                        </span>
                      )}
                    </p>
                  </motion.div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="grid grid-cols-1 md:grid-cols-2 gap-6"
              >
                <motion.div
                  whileFocus={{ scale: 1.02 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <label className="block text-sm font-semibold text-gray-700 mb-2">{t('full_name')} *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full p-3 border border-gray-300 rounded-xl bg-white/50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all hover:shadow-md"
                    placeholder={t('enter_full_name')}
                    required
                  />
                </motion.div>

                <motion.div
                  whileFocus={{ scale: 1.02 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <label className="block text-sm font-semibold text-gray-700 mb-2">{t('email_address')} *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full p-3 border border-gray-300 rounded-xl bg-white/50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all hover:shadow-md"
                    placeholder={t('enter_email')}
                    required
                  />
                </motion.div>

                <motion.div
                  whileFocus={{ scale: 1.02 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('password')} {!user && '*'}
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full p-3 border border-gray-300 rounded-xl bg-white/50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all hover:shadow-md"
                    placeholder={user ? t('keep_current_password') : t('enter_password')}
                    required={!user}
                  />
                </motion.div>

                <motion.div
                  whileFocus={{ scale: 1.02 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <label className="block text-sm font-semibold text-gray-700 mb-2">{t('phone_number')}</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full p-3 border border-gray-300 rounded-xl bg-white/50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all hover:shadow-md"
                    placeholder="081234567890"
                  />
                </motion.div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                whileFocus={{ scale: 1.02 }}
              >
                <label className="block text-sm font-semibold text-gray-700 mb-2">{t('account_status')}</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-xl bg-white/50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all hover:shadow-md"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-gray-200"
              >
                <motion.button
                  type="submit"
                  disabled={loading}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`flex-1 text-white px-6 py-3 rounded-xl font-semibold focus:ring-4 transition-all duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl ${isDark ? 'bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 focus:ring-gray-500' : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:ring-blue-200'}`}
                >
                  {loading ? (
                    <>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        className="w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-2"
                      />
                      {t('saving')}
                    </>
                  ) : (
                    <>
                      <motion.svg
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.9 }}
                        className="w-5 h-5 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </motion.svg>
                      {user ? t('update_user') : t('create_user')}
                    </>
                  )}
                </motion.button>
                <motion.button
                  type="button"
                  onClick={onCancel}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 focus:ring-4 focus:ring-gray-200 transition-all duration-200 flex items-center justify-center"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  {t('cancel')}
                </motion.button>
              </motion.div>
            </motion.form>
          </motion.div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}